/* eslint-disable eslint-comments/disable-enable-pair -- carrier card */

import "./carrier-card.css";

import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	chevronDown,
	chevronRight,
	xClose,
} from "@tql/ui-components-react/untitled/icons";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import type {
	CarrierSelectionPageLoadData,
	InsuranceMarkupVariables,
} from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import {
	insuranceMarkupMutationKey,
	useGetInsuranceMarkup,
} from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import { getCarrierLogoUrl } from "~/features/quote/utils/carrier-selection-utils.ts";
import type { MarginCalculationData } from "~/features/quote/utils/margin-calculator.ts";
import { calculateMargin } from "~/features/quote/utils/margin-calculator.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { Carrier, CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";

import { LoadValueModal } from "../../../shipment-details/components/load-value-modal/load-value-modal.tsx";
import { carrierLiabilityIsInsufficient } from "../../utils/carrier-liability.ts";
import { getCarrierServiceLevelDisplayName } from "../../utils/get-carrier-service-level-display-name.ts";
import { CarrierCardLayout } from "../shared/carrier-card-layout.tsx";
import { CarrierNameSection } from "./components/carrier-name-section.tsx";
import { ExpansionSection } from "./components/expansion-section/expansion-section.tsx";
import {
	useCarrierAutosave,
	useCarrierBooking,
	useCarrierManagement,
} from "./hooks/use-carrier-actions.tsx";
import { useCarrierBanner } from "./hooks/use-carrier-banner.tsx";
import { useCarrierCardAction } from "./hooks/use-carrier-card-action.ts";
import { useCarrierCardColumns } from "./hooks/use-carrier-card-columns.tsx";
import {
	useCarrierCardState,
	useCarrierTerminals,
	useCarrierValidation,
} from "./hooks/use-carrier-ui.ts";
import { getCarrierCardAction } from "./utils/carrier-card-action.ts";

const getRequiredIndicator = (
	carrierPrice: CarrierPrice,
	isReratePrevCarrier?: boolean,
) =>
	!isReratePrevCarrier && carrierPrice.id <= 0 ? (
		<span className={"required"}>&nbsp;*</span>
	) : null;

const getCarrierCardClassName = (
	isSaving: boolean,
	isInsuranceMarkupPending: boolean,
	isSelected: boolean,
) =>
	[
		isSaving || isInsuranceMarkupPending ? "saving" : "",
		isSelected ? "selected" : "",
	]
		.filter(Boolean)
		.join(" ");

const getMobileWrapperClassName = (
	isTabletOrMobile: boolean,
	isCompactMobile: boolean,
) => {
	if (isCompactMobile) {
		return "carrier-card-wrapper-mobile carrier-card-wrapper-compact-mobile";
	}
	if (isTabletOrMobile) {
		return "carrier-card-wrapper-mobile";
	}
	return undefined;
};

export type CarrierCardProps = {
	carrierPrice: CarrierPrice;
	onStepChange: () => void;
	onBookingStart?: () => void;
	onBookingEnd?: () => void;
	pageData: CarrierSelectionPageLoadData | undefined;
	marginCalculationData?: MarginCalculationData;
	isSimulated?: boolean;
	isBookingDisabled?: boolean;
	isReadOnly?: boolean;
	bannerType?: string | null;
	enableShipmentValueModal?: boolean;
	enableTenderOnBook?: boolean;
	onTenderSuccess?: () => void;
	onTenderError?: (message: string) => void;
	onPaymentRequired?: (quote: QuoteResponse) => void;
	isReratePrevCarrier?: boolean;
	isRerate?: boolean;
	onSelectCarrier?: (carrier: CarrierPrice) => void;
	isSelected?: boolean;
};

export function CarrierCard({
	carrierPrice,
	onStepChange,
	onBookingStart,
	onBookingEnd,
	pageData,
	marginCalculationData,
	isSimulated = false,
	isBookingDisabled = false,
	isReadOnly = false,
	bannerType = null,
	enableShipmentValueModal = false,
	enableTenderOnBook = false,
	onTenderSuccess,
	onTenderError,
	onPaymentRequired,
	isReratePrevCarrier,
	isRerate = false,
	onSelectCarrier,
	isSelected = false,
}: Readonly<CarrierCardProps>) {
	const quoteDetailsCtx = useContext(QuoteDetailsContext);
	const {
		updateCarrierPriceMargin,
		updateAdHocCarrier: updateAdHocCarrierContext,
	} = quoteDetailsCtx;
	const { isInternal } = useDeploymentType();
	const isTabletOrMobile = useIsMobile(992);
	const isCompactMobile = useIsMobile(640);
	const baseUrl = getBaseUrl();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mutateAsync: getInsuranceMarkup } = useGetInsuranceMarkup();
	// Track in-flight insurance markup calls so the loader survives re-renders.
	const isInsuranceMarkupPending =
		useIsMutating({
			mutationKey: insuranceMarkupMutationKey,
			predicate: (mutation) => {
				const variables = mutation.state.variables as
					| InsuranceMarkupVariables
					| undefined;
				return variables?.carrierPriceId === carrierPrice.id;
			},
		}) > 0;

	const { cardRef, isExpanded, isAdHocCarrier, setIsExpanded, handleKeyDown } =
		useCarrierCardState({ carrierPrice });
	const isBookClickPendingRef = useRef(false);

	const [localCarrierQuoteId, setLocalCarrierQuoteId] = useState(
		carrierPrice.carrierQuoteId ?? "",
	);

	useEffect(() => {
		setLocalCarrierQuoteId(carrierPrice.carrierQuoteId ?? "");
	}, [carrierPrice.carrierQuoteId]);

	const { originTerminal, destinationTerminal, isTerminalDetailsLoading } =
		useCarrierTerminals({
			carrierPrice,
			isExpanded,
		});

	const {
		handleBookCarrierClick,
		refreshModal: bookingRefreshModal,
		isBooking,
	} = useCarrierBooking({
		carrierPrice,
		onStepChange,
		onBookingStart,
		onBookingEnd,
		enableTenderOnBook,
		onTenderSuccess,
		onTenderError,
		onPaymentRequired,
	});

	const { isDisabled, isComplete } = useCarrierValidation(carrierPrice);
	const {
		removeBlankCarrier,
		quoteState,
		applyFoundCarrier,
		updateAdHocCarrier,
		customerId,
		getSearchCarrierRoute,
	} = useCarrierManagement();
	const isBookedCarrier = quoteState.selectedCarrierPriceId === carrierPrice.id;
	// In the combined book+tender flow, selecting a carrier does not finalize the
	// shipment — only a successful tender (which returns a PO number) does. Treat a
	// carrier as finalized ("Tendered" + disabled) only once the shipment actually
	// has a PO number; otherwise loading a quote that had a carrier selected but
	// was never tendered would wrongly disable its Tender button. The legacy
	// book-only flow keeps prior behavior (selecting a carrier = booked).
	const isCarrierFinalized =
		isBookedCarrier && (!enableTenderOnBook || quoteState.poNumber != null);

	const searchCarrierRoute = (searchTerm: string) =>
		getSearchCarrierRoute(searchTerm, {
			onlyLTL: true,
			excludeNoLoadCarriersForCustomerId: customerId,
		});
	const { isSaving, save } = useCarrierAutosave(
		carrierPrice,
		isComplete,
		quoteState.id,
		quoteState.eTag,
	);

	const handleCarrierSelection = async (carrier: Carrier) => {
		// Apply the carrier to update the state
		applyFoundCarrier(carrier);

		// Fetch insurance markup immediately
		const insuranceMarkupResult = await getInsuranceMarkup({
			legacyId: quoteState.id,
			carrierId: carrier.id,
			carrierPriceId: carrierPrice.id,
		});

		if (insuranceMarkupResult.success) {
			// Update the carrier with the insurance markup values
			// These values will be available on the carrier object when autosave is triggered
			// Use carrierPrice.id (not carrier.id) because the carrierPrice maintains its original negative id
			updateAdHocCarrierContext(
				carrierPrice.id,
				"maxLiabilityNew",
				insuranceMarkupResult.data.maxLiabilityNew,
			);
			updateAdHocCarrierContext(
				carrierPrice.id,
				"maxLiabilityUsed",
				insuranceMarkupResult.data.maxLiabilityUsed,
			);
			// Persist the override so the effective-liability helper (used by the
			// modal guard, banner/icon, and modal copy) evaluates this carrier
			// consistently instead of falling back to New/Used.
			updateAdHocCarrierContext(
				carrierPrice.id,
				"maxLiabilityOverride",
				insuranceMarkupResult.data.maxLiabilityOverride,
			);
			updateAdHocCarrierContext(
				carrierPrice.id,
				"insuranceMarkup",
				insuranceMarkupResult.data.insuranceMarkup,
			);
		} else {
			console.error(
				"Error fetching insurance markup:",
				insuranceMarkupResult.error,
			);
		}
	};

	const handleBlur = () => {
		if (isAdHocCarrier && !isBookClickPendingRef.current) {
			void save();
		}
	};

	const handleBookMouseDown = () => {
		isBookClickPendingRef.current = true;
	};

	const {
		handleAction,
		showLoadValueModal,
		handleLoadValueModalClose,
		handleLoadValueModalAccept,
		handleInsuranceAdded,
	} = useCarrierCardAction({
		carrierPrice,
		onSelectCarrier,
		isReratePrevCarrier,
		poNumber: quoteState.poNumber,
		enableShipmentValueModal,
		handleBookCarrierClick,
	});

	// Show a red shield-off warning on this carrier's Max Liability when the
	// shipment value exceeds the carrier's effective liability. Uses the same
	// condition as the Shipment Value modal, but is not tied to its feature flag.
	const showLiabilityWarning = carrierLiabilityIsInsufficient(
		carrierPrice,
		quoteState.insuranceValue,
		quoteState.insuranceApplyTypeId,
		quoteState.insuranceProductTypeId,
	);

	const handleBookClick = () => {
		if (isReratePrevCarrier) {
			if (quoteState.poNumber == null) {
				navigate(routes.track);
			} else {
				// Drop the cached shipment so the destination page shows its loading
				// spinner while it refetches the rerated data.
				queryClient.removeQueries({ queryKey: ["shipment"] });
				navigate(
					routes.shipmentManagement.replace(
						":poNumber",
						String(quoteState.poNumber),
					),
				);
			}
			return;
		}
		isBookClickPendingRef.current = false;
		handleAction();
	};

	const handleCarrierRateChange = (newCarrierRate: number | null) => {
		// Update the carrier rate
		updateAdHocCarrier(carrierPrice.id, "carrierRate", newCarrierRate ?? 0);

		// Auto-calculate and update margin only when user changes carrier rate
		if (newCarrierRate && newCarrierRate > 0 && marginCalculationData) {
			const margin = calculateMargin(
				{ ...carrierPrice, carrierRate: newCarrierRate },
				marginCalculationData.ltlMarkups,
				marginCalculationData.customerLtlMarkups,
				marginCalculationData.partialsMarkups,
				marginCalculationData.customerPartialsMarkups,
			);
			updateAdHocCarrier(carrierPrice.id, "brokerMargin", margin);
		}
	};
	const carrierPriceDisplayName = useMemo(
		() => getCarrierServiceLevelDisplayName(carrierPrice),
		[carrierPrice],
	);

	const isExpired = useMemo(() => {
		const expDateStr = carrierPrice.carrierQuoteExpirationDate;
		if (!expDateStr) return false;
		const expDate = new Date(expDateStr);
		if (Number.isNaN(expDate.getTime())) return false;
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		expDate.setHours(0, 0, 0, 0);
		return expDate < today;
	}, [carrierPrice.carrierQuoteExpirationDate]);

	const requiredIndicator = getRequiredIndicator(
		carrierPrice,
		isReratePrevCarrier,
	);

	const overlay =
		isSaving || isInsuranceMarkupPending ? (
			<div className={"carrier-card-saving-overlay"}>
				<Loader size={"medium"} />
			</div>
		) : null;

	const leftControl = (
		<div className={"carrier-card-expansion-button"}>
			<SvgIcon
				icon={isExpanded ? chevronDown : chevronRight}
				onClick={() => setIsExpanded(!isExpanded)}
				size={"xxlarge"}
			/>
		</div>
	);

	const carrierLogoUrl = getCarrierLogoUrl(carrierPrice.carrier.scac, baseUrl);

	const logo = (
		<div className={"carrier-card-logo-wrapper"}>
			{isAdHocCarrier && isDisabled ? (
				<div className={"adhoc-carrier-logo"}></div>
			) : (
				<img
					alt={"Carrier Logo"}
					className={"carrier-card-image"}
					src={carrierLogoUrl}
				/>
			)}
		</div>
	);

	const nameSection = (
		<CarrierNameSection
			carrierPrice={carrierPrice}
			isAdHocCarrier={isAdHocCarrier}
			isDisabled={isDisabled}
			onCarrierSelection={(carrier) => void handleCarrierSelection(carrier)}
			searchCarrierRoute={searchCarrierRoute}
		/>
	);

	const columns = useCarrierCardColumns({
		carrierPrice,
		carrierPriceDisplayName,
		isInternal,
		isAdHocCarrier,
		isDisabled,
		isSimulated,
		isReadOnly,
		isReratePrevCarrier,
		showLiabilityWarning,
		requiredIndicator,
		localCarrierQuoteId,
		pageData,
		handleBlur,
		handleCarrierRateChange,
		setLocalCarrierQuoteId,
		updateAdHocCarrier,
		updateCarrierPriceMargin,
	});

	const action = getCarrierCardAction({
		isSelection: Boolean(onSelectCarrier),
		isSelected,
		isBookingDisabled,
		isReratePrevCarrier,
		enableTenderOnBook,
		isRerate,
		isCarrierFinalized,
		isExpired,
		isSimulated,
		isAdHocCarrier,
		isComplete,
	});

	const footer = (
		<div
			className={"book-button"}
			style={isReadOnly ? { visibility: "hidden" } : undefined}
		>
			<Button
				disabled={action.disabled}
				loading={isBooking}
				onClick={handleBookClick}
				onMouseDown={handleBookMouseDown}
				variant={action.variant}
			>
				{action.label}
			</Button>
		</div>
	);

	const afterContent = (
		<>
			{isAdHocCarrier && carrierPrice.id < 0 ? (
				<SvgIcon
					className={"carrier-card-remove"}
					icon={xClose}
					onClick={() => removeBlankCarrier(carrierPrice.id)}
				/>
			) : null}
			{isExpanded ? (
				<div className={"carrier-card-expansion"}>
					<ExpansionSection
						isLoading={isTerminalDetailsLoading}
						terminal={originTerminal}
						type={"Origin"}
					/>
					<ExpansionSection
						isLoading={isTerminalDetailsLoading}
						terminal={destinationTerminal}
						type={"Destination"}
					/>
				</div>
			) : null}
		</>
	);

	const { banner, bannerClassName } = useCarrierBanner(
		bannerType,
		carrierPrice,
	);
	const carrierCardClassName = getCarrierCardClassName(
		isSaving,
		isInsuranceMarkupPending,
		isSelected,
	);
	const wrapperClassName = getMobileWrapperClassName(
		isTabletOrMobile,
		isCompactMobile,
	);

	return (
		<>
			<CarrierCardLayout
				afterContent={afterContent}
				banner={banner}
				bannerClassName={bannerClassName}
				cardProps={
					carrierCardClassName ? { className: carrierCardClassName } : undefined
				}
				columns={columns.filter((col) => col.shouldShow !== false)}
				footer={footer}
				leftControl={leftControl}
				logo={logo}
				nameSection={nameSection}
				overlay={overlay}
				wrapperProps={{
					"aria-label": `Carrier card for ${carrierPrice.carrier.name}`,
					className: wrapperClassName,
					onKeyDown: handleKeyDown,
					role: "button",
					tabIndex: 0,
				}}
				wrapperRef={cardRef}
			/>
			{bookingRefreshModal}
			{showLoadValueModal ? (
				<LoadValueModal
					onAccept={handleLoadValueModalAccept}
					onClose={handleLoadValueModalClose}
					onInsuranceAdded={handleInsuranceAdded}
					quoteId={quoteDetailsCtx.quoteState.id}
					selectedCarrierPrice={carrierPrice}
					shipmentValue={quoteDetailsCtx.quoteState.insuranceValue}
					showTenderLabel={enableTenderOnBook}
				/>
			) : null}
		</>
	);
}
